'use client'

import FreestarAdSlot from '@freestar/pubfig-adslot-react-component'

import React, { useEffect, useState } from 'react'

import useRegion from '../../hooks/useRegion'
import useWindowDimension from '../../hooks/useWindowDimension'
import bannerStyle from '../../styles/components/ads/banner'
import breakpoints from '../../theme/breakpoints'
import adConfig, { TAdConfigType, Tag } from '../../utils/ads/adConfig'
import mapAdUnitName from '../../utils/ads/freestarAdMap'
import { useEnvironmentContext } from '../EnvironmentProvider'

import dynamic from 'next/dynamic'
import Script from 'next/script'

const GAMAdBase = dynamic(() => import('./GAMAdBase'), { ssr: false })

enum adType {
  taboola = 'taboola',
  /* 'leaderboard' prop is temporarily now,
     it's here just to not break the layouts other than Article.
     sooner or later will be deprecated and
     'leaderboardNew' will be used app wide
    */
  leaderboard = 'leaderboard',
  leaderboardNew = 'leaderboardNew',
  middleboard = 'middleboard',
  postscript = 'postscript',
  homePageSidebar = 'homePageSidebar',
  sidebar = 'sidebar',
  sidebar1 = 'sidebar1',
  sidebar2 = 'sidebar2',
  custom = 'custom',
  footer = 'footer',
  storiesCollection = 'storiesCollection'
}

type Node = {
  attribs: {
    'data-config'?: string
    'data-size': keyof typeof adType
  }
}

const TABOOLA_SCRIPT = `
  window._taboola = window._taboola || [];
  _taboola.push({article:'auto'});
  !function (e, f, u, i) {
    if (!document.getElementById(i)){
      e.async = 1;
      e.src = u;
      e.id = i;
      f.parentNode.insertBefore(e, f);
    } }(document.createElement('script'),
  document.getElementsByTagName('script')[0],
  '//cdn.taboola.com/libtrc/news12optimum-network/loader.js',
  'tb_loader_script');
  if(window.performance && typeof window.performance.mark == 'function')
    {window.performance.mark('tbl_ic');}
`

type Config = {
  mode?: string
  container?: string
  placement?: string
  target_type?: string
}

const configFor = (node: Node): Config => {
  try {
    return JSON.parse(node.attribs['data-config'] ?? '{}') || {}
  } catch {
    return {}
  }
}

type AdParams = {
  width: number
  type: TAdConfigType | 'taboola'
  id: string
  template?: string
  config: Config
  tags?: Tag[]
  contentfulCategories?: string[]
  region?: string
}

const renderAd = ({ width, type, id, template, config, tags, contentfulCategories, region }: AdParams) => {
  const { freestarEnabled } = useEnvironmentContext()
  const isSidebar = type === adType.sidebar1 || type === adType.sidebar2

  if ((isSidebar && width < breakpoints.lg) || (type === adType.postscript && width > breakpoints.lg)) {
    return null
  }

  if (type === adType.taboola) {
    return (
      <div data-testid={`${type}-ad`} id={config.container}>
        <Script
          dangerouslySetInnerHTML={{
            __html: `
          window._taboola = window._taboola || [];
          _taboola.push(${JSON.stringify(config)});
          _taboola.push({flush: true});
        `
          }}
        />
      </div>
    )
  }

  if (freestarEnabled) {
    const config = adConfig(type, template, tags, contentfulCategories, id)
    return (
      <div data-testid={`${type}-ad`} className={`banner banner-${type}`}>
        {region && (
          <FreestarAdSlot
            publisher={'news12-com'}
            placementName={`news12_${mapAdUnitName(id)}`}
            channel={region?.toUpperCase() || 'LI'}
            targeting={config.targeting}
          />
        )}
      </div>
    )
  }

  return (
    <>
      <div data-testid={`${type}-ad`} className={`banner banner-${type}`}>
        <GAMAdBase config={{ ...adConfig(type, template, tags, contentfulCategories, id), id }} />
      </div>
      <style jsx>{bannerStyle}</style>
    </>
  )
}

type AdProps = {
  node: Node
  id: string
  tags?: Tag[]
  contentfulCategories?: string[]
  region?: string
}

const Ad = ({ id, node, tags, contentfulCategories }: AdProps) => {
  const { freestarEnabled } = useEnvironmentContext()
  const { shortName, isReadyOnClientSide } = useRegion()
  const [regionShortName, setRegionShortName] = useState<string>(shortName || '')
  useEffect(() => {
    if (freestarEnabled && isReadyOnClientSide) {
      setRegionShortName(shortName || '')
    }
  }, [freestarEnabled, regionShortName, isReadyOnClientSide])
  const { width = 0 } = useWindowDimension()

  const adParams = {
    type: node.attribs['data-size'],
    template: 'default',
    config: configFor(node),
    width,
    id,
    tags,
    contentfulCategories,
    region: regionShortName
  }

  return (
    <div className={`banner-${adParams.type}-area`}>
      <Script dangerouslySetInnerHTML={{ __html: TABOOLA_SCRIPT }} />
      {renderAd(adParams)}
    </div>
  )
}

export default Ad
